import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { 
  CheckCircle, ChevronRight, Dumbbell, MessageSquareText, 
  RotateCcw, Star, X, AlertTriangle, Award, Sparkles 
} from "lucide-react";
import { AiWorkoutGeneratorDialog } from "@/components/client/AiWorkoutGeneratorDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { DesktopSidebar } from "@/components/DesktopSidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useEnforceLegalApproval } from "@/hooks/use-enforce-legal";
import { useAuth } from "@/hooks/useAuth";
import {
  listWorkoutAssigned,
  type WorkoutAssigned,
  type WorkoutAssignedGroupingData,
} from "@/integrations/backend/workout-assigned";
import { parseYYYYMMDDToLocalDate } from "@/lib/date-only";
import { createEnergyLevelChoice, type EnergyLevelChoiceValue } from "@/integrations/backend/energy-level-choice";

const StartWorkout = () => {
  useEnforceLegalApproval();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [energyLevel, setEnergyLevel] = useState("");
  const [showEnergyDialog, setShowEnergyDialog] = useState(true);
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [injury, setInjury] = useState("");
  const [showInjuryDetails, setShowInjuryDetails] = useState(false);
  const [injuryDetails, setInjuryDetails] = useState("");
  const [animateEnergy, setAnimateEnergy] = useState(false);
  const [workoutAssignments, setWorkoutAssignments] = useState<WorkoutAssigned[]>([]);
  const [loadingWorkouts, setLoadingWorkouts] = useState(true);
  /** Tracks which ?assigned= value the latest list fetch completed for (null = no param). */
  const [fetchedAssignedId, setFetchedAssignedId] = useState<string | null | undefined>(undefined);
  const isMobile = useIsMobile();

  // Format date relatively (Today, Tomorrow, Wednesday, Wednesday, Dec 1st)
  const formatRelativeDate = (dateString: string): string => {
    const workoutDate = parseYYYYMMDDToLocalDate(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    workoutDate.setHours(0, 0, 0, 0);
    
    const diffTime = workoutDate.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays === -1) return "Yesterday";
    if (diffDays > 1 && diffDays <= 7) {
      return workoutDate.toLocaleDateString('en-US', { weekday: 'long' });
    }
    // For dates more than a week away, show "Wednesday, Dec 1st" format
    return workoutDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  // Filter scheduled workouts (date >= today, not completed)
  const scheduledWorkouts = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return workoutAssignments
      .filter((w) => {
        const workoutDate = parseYYYYMMDDToLocalDate(w.date);
        workoutDate.setHours(0, 0, 0, 0);
        const isCompleted = w.completed_workout !== null;
        return workoutDate >= today && !isCompleted;
      })
      .sort((a, b) => {
        // Sort by date ascending (oldest first)
        const dateA = parseYYYYMMDDToLocalDate(a.date).getTime();
        const dateB = parseYYYYMMDDToLocalDate(b.date).getTime();
        return dateA - dateB;
      });
  }, [workoutAssignments]);

  // Get the selected workout from query params, or fall back to next incomplete workout
  const selectedWorkoutId = searchParams.get("assigned");
  const selectedWorkout = useMemo(() => {
    if (selectedWorkoutId) {
      return workoutAssignments.find(w => w.id === selectedWorkoutId) || null;
    }
    return null;
  }, [selectedWorkoutId, workoutAssignments]);

  // When ?assigned= is present, never silently substitute another workout.
  // Wait until a fetch for this exact assigned id has finished before treating it as missing.
  const listReadyForSelection = fetchedAssignedId === selectedWorkoutId;
  const assignedMissing =
    listReadyForSelection &&
    !loadingWorkouts &&
    selectedWorkoutId !== null &&
    selectedWorkout === null;

  // Get the next incomplete workout (first scheduled workout) - only if no specific workout is selected
  const nextWorkout = useMemo(() => {
    if (selectedWorkoutId) return selectedWorkout;
    if (scheduledWorkouts.length === 0) return null;
    return scheduledWorkouts[0];
  }, [selectedWorkoutId, selectedWorkout, scheduledWorkouts]);

  // Determine if we're showing a specific selected workout (vs the next workout)
  const isShowingSelectedWorkout = selectedWorkoutId !== null && selectedWorkout !== null;

  const getGroupingLabel = (grouping: WorkoutAssignedGroupingData): string | null => {
    const exerciseCount = grouping.exercises?.length || 0;
    if (exerciseCount >= 3) return "Circuit";
    if (exerciseCount === 2) return "Superset";
    return null;
  };

  // Get workout groupings for the next workout, keeping supersets/circuits grouped
  const workoutPreviewItems = useMemo(() => {
    if (!nextWorkout) return [];
    return (nextWorkout.groupings || []).map((grouping) => ({
      id: grouping.id,
      label: getGroupingLabel(grouping),
      rounds: grouping.sets || 0,
      exercises: (grouping.exercises || []).map((exercise) => ({
        id: exercise.id,
        name: exercise.exercise_name,
        sets: exercise.sets || grouping.sets || 0,
      })),
    }));
  }, [nextWorkout]);

  // Fetch scheduled workouts for the current client (also when ?assigned= changes after AI generate)
  useEffect(() => {
    if (!profile?.id) {
      setLoadingWorkouts(false);
      setFetchedAssignedId(selectedWorkoutId);
      setWorkoutAssignments([]);
      return;
    }

    const requestedAssignedId = selectedWorkoutId;
    let active = true;
    (async () => {
      try {
        setLoadingWorkouts(true);
        const assignments = await listWorkoutAssigned(profile.id);
        if (active) {
          setWorkoutAssignments(assignments);
          setFetchedAssignedId(requestedAssignedId);
        }
      } catch (error) {
        console.error("Failed to load scheduled workouts", error);
        if (active) {
          setWorkoutAssignments([]);
          setFetchedAssignedId(requestedAssignedId);
        }
      } finally {
        if (active) {
          setLoadingWorkouts(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [profile?.id, selectedWorkoutId]);

  // Energy levels with more engaging names and descriptions
  const energyOptions = [
    { 
      value: "supercharged", 
      label: "Supercharged! 💪", 
      description: "I'm feeling stronger than usual today!",
      avatar: "😎",
      color: "emerald-500",
      message: "Awesome! Let's increase the intensity today!"
    },
    { 
      value: "ready", 
      label: "Ready to crush it! 🔥", 
      description: "I'm feeling good and ready to go!",
      avatar: "🔥",
      color: "emerald-400",
      message: "Great! Let's stick to your regular plan."
    },
    { 
      value: "tired", 
      label: "A bit tired... 😴", 
      description: "I'm more tired than normal but can still workout.",
      avatar: "😴",
      color: "orange-400",
      message: "No problem! We'll adjust the intensity slightly."
    },
    { 
      value: "low", 
      label: "Tank's on empty ⚠️", 
      description: "I'm feeling weak or sore today.",
      avatar: "⚠️",
      color: "red-400",
      message: "Let's focus on recovery and light movement today."
    },
    { 
      value: "injured", 
      label: "Dealing with an injury ⚕️", 
      description: "I have a specific issue or injury to work around.",
      avatar: "⚕️",
      color: "purple-400",
      message: "Thanks for letting us know. We'll modify your workout accordingly."
    },
  ];

  // Fun encouraging tips based on energy level
  const getEncouragingTip = () => {
    switch(energyLevel) {
      case "supercharged":
        return "Studies show adding 10-15% more weight than usual when you're feeling supercharged can lead to breakthrough strength gains! 🚀";
      case "ready":
        return "Music with 120-140 BPM (like 'Eye of the Tiger') can boost your performance by up to 15%! 🎵 Time to crush those reps!";
      case "tired":
        return "Deep breathing for 60 seconds before starting can increase your oxygen uptake and give you an energy boost! Try it now. 🧘‍♂️";
      case "low":
        return "A banana or handful of dates provides quick natural sugars to fuel your workout when energy is low! 🍌 Plus, they're packed with potassium!";
      case "injured":
        return "Research shows that gentle movement around injured areas improves blood flow and can speed up your recovery by 20-30%! 🩹";
      default:
        return "Let us know how you're feeling today to get a personalized workout experience! 💪";
    }
  };

  // Common injury locations 
  const injuryLocations = [
    "Shoulder", "Lower back", "Knee", "Ankle", "Wrist",
    "Neck", "Hip", "Elbow", "Upper back", "Other"
  ];

  const handleEnergySelect = async (value: string) => {
    setAnimateEnergy(true);
    setEnergyLevel(value);
    
    // Save the energy level choice to the backend
    try {
      await createEnergyLevelChoice(value as EnergyLevelChoiceValue);
    } catch (error) {
      console.error("Failed to save energy level choice:", error);
      // Continue with UI update even if save fails
    }
    
    // If injured, show injury details form
    if (value === "injured") {
      setShowInjuryDetails(true);
    } else {
      setShowEnergyDialog(false);
    }
    
    // Reset animation after it completes
    setTimeout(() => setAnimateEnergy(false), 600);
  };

  const handleInjurySubmit = () => {
    setShowInjuryDetails(false);
    setShowEnergyDialog(false);
  };

  const startAiWorkoutGenerator = () => {
    setShowAiDialog(true);
  };

  const selectedOption = energyOptions.find(option => option.value === energyLevel);

  return (
    <div className="min-h-screen gradient-dark relative overflow-hidden">
      <DesktopSidebar activeTab="none" />
      
      {/* Full-screen energy level selection experience */}
      <Dialog open={showEnergyDialog} onOpenChange={setShowEnergyDialog}>
        <DialogContent className="max-w-md max-h-[85vh] rounded-2xl p-0 overflow-hidden flex flex-col">
          <div className="gradient-primary p-4 text-black">
            <DialogHeader>
              <DialogTitle className="text-xl text-center text-black">How are you feeling today?</DialogTitle>
            </DialogHeader>
            <div className="text-center mb-2 text-sm text-black/70">
              We'll personalize your workout based on your energy level
            </div>
          </div>
          
          <div className="p-4 overflow-y-auto min-h-0">
            <RadioGroup 
              value={energyLevel} 
              onValueChange={handleEnergySelect} 
              className="gap-2"
            >
              {energyOptions.map((option) => (
                <label
                  key={option.value}
                  className={cn(
                    "flex items-center space-x-2 border rounded-xl p-3 cursor-pointer transition-all",
                    energyLevel === option.value ? "border-primary bg-primary/10 animate-scale-in" : "border-primary/20 bg-dark-card",
                    "hover:border-primary/50 hover:bg-dark-slate"
                  )}
                >
                  <RadioGroupItem value={option.value} id={option.value} className="text-primary" />
                  <div className="flex items-center flex-1">
                    <div className={cn(
                      "flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center mr-2",
                      "bg-primary/20 text-primary"
                    )}>
                      <span className="text-xl">{option.avatar}</span>
                    </div>
                    <div>
                      <span className="font-medium">{option.label}</span>
                      <span className="text-gray-light text-xs block">{option.description}</span>
                    </div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>
        </DialogContent>
      </Dialog>

      {/* Injury details dialog */}
      <Dialog open={showInjuryDetails} onOpenChange={setShowInjuryDetails}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl text-center">Tell us about your injury</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-2">Where is your injury? (Select all that apply)</p>
              <div className="grid grid-cols-2 gap-2">
                {injuryLocations.map((location) => (
                  <Button
                    key={location}
                    type="button"
                    variant={injury === location ? "default" : "outline"}
                    className={cn(
                      "justify-start",
                      injury === location ? "bg-primary text-black" : ""
                    )}
                    onClick={() => setInjury(location)}
                  >
                    {injury === location && <CheckCircle className="h-4 w-4 mr-2" />}
                    {location}
                  </Button>
                ))}
              </div>
            </div>
            
            <div>
              <p className="text-sm font-medium mb-2">Details about your injury/issue</p>
              <Textarea 
                placeholder="Describe your injury or limitation (e.g., 'My left shoulder hurts when I raise my arm above my head')"
                value={injuryDetails}
                onChange={(e) => setInjuryDetails(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
            
            <div className="flex justify-end">
              <Button 
                className="bg-primary text-black hover:bg-primary/90"
                onClick={handleInjurySubmit}
              >
                Submit & Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AiWorkoutGeneratorDialog open={showAiDialog} onOpenChange={setShowAiDialog} />

      {/* Main content */}
      <div className={`pt-6 px-4 ${isMobile ? "pb-client-nav" : ""} ${!isMobile ? "md:ml-16" : ""}`}>
        <div className="max-w-md mx-auto">
          {/* Header with compact workout mascot */}
          <div className="flex items-center justify-between mb-6">
            <Link to="/client-plan" className="text-gray-600">
              <Button variant="ghost" size="sm">
                <X className="h-5 w-5" />
              </Button>
            </Link>
            <div className="text-center flex-1 flex items-center justify-center gap-2">
              <h1 className="text-2xl font-bold">Start Workout</h1>
              {/* Small integrated mascot icon */}
              <div className={cn(
                "gradient-primary w-8 h-8 rounded-full flex items-center justify-center shadow-md",
                animateEnergy ? "animate-scale-in" : ""
              )}>
                {energyLevel === "injured" ? (
                  <AlertTriangle className="h-4 w-4 text-black" />
                ) : (
                  <Dumbbell className="h-4 w-4 text-black" />
                )}
              </div>
            </div>
            <div className="w-10"></div> {/* Empty div for balance */}
          </div>
          
          {/* Motivational quote banner - replaces the large mascot */}
          {!energyLevel && (
            <div 
              className="mb-6 bg-dark-slate rounded-lg p-3 shadow-sm border border-primary/20 animate-pulse cursor-pointer hover:bg-dark-card transition-colors"
              onClick={() => setShowEnergyDialog(true)}
            >
              <div className="flex items-center">
                <div className="rounded-full bg-primary p-1.5 mr-3">
                  <Sparkles className="h-4 w-4 text-black" />
                </div>
                <p className="text-white font-medium text-sm">
                  Let us know how you're feeling today to personalize your workout!
                </p>
              </div>
            </div>
          )}
          
          {/* Energy level indicator (if selected) - MOVED TO TOP */}
          {energyLevel && (
            <div className="mb-6 animate-fade-in">
              <div className="bg-dark-card border border-primary/20 rounded-lg p-4 shadow-md">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className={`h-3 w-3 rounded-full mr-3 ${
                      energyLevel === "supercharged" ? "bg-primary" :
                      energyLevel === "ready" ? "bg-primary/80" :
                      energyLevel === "tired" ? "bg-primary/60" :
                      energyLevel === "injured" ? "bg-primary/50" :
                      "bg-primary/40"
                    }`}></div>
                    <div>
                      <h4 className="font-medium">Today's Energy Level</h4>
                      <p className="text-sm text-gray-light">
                        {energyOptions.find(o => o.value === energyLevel)?.label}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowEnergyDialog(true)}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* New encouraging tip based on energy level */}
                <div className="bg-dark-slate rounded-lg p-3 border border-primary/20">
                  <div className="flex">
                    <div className="flex-shrink-0 mr-3">
                      <Award className="h-5 w-5 text-primary" />
                    </div>
                    <p className="text-sm text-white">{getEncouragingTip()}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Injury details (if provided) */}
          {energyLevel === "injured" && injury && (
            <div className="mb-6 animate-fade-in">
              <div className="bg-dark-card rounded-lg p-4 shadow-md border-l-4 border-primary">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-primary mr-3 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Injury Note</h4>
                    <p className="text-sm text-gray-light">
                      {injury}{injuryDetails ? `: ${injuryDetails}` : ""}
                    </p>
                    <p className="text-sm text-primary mt-2">
                      Workout modified to avoid {injury.toLowerCase()} strain
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Next workout card */}
          {nextWorkout ? (
            <Card className={cn("mb-6 border-primary/20 bg-dark-card shadow-card overflow-hidden", energyLevel ? "animate-fade-in" : "")}>
              {!isShowingSelectedWorkout && (
                <div className="gradient-primary p-4">
                  <h2 className="text-black text-xl font-bold flex items-center">
                    <Star className="mr-2 h-5 w-5 text-black" /> 
                    Your Next Workout for {formatRelativeDate(nextWorkout.date)}
                  </h2>
                </div>
              )}
              <CardContent className="p-4">
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-bold text-lg">{nextWorkout.name}</h3>
                    <Badge variant="outline" className="bg-transparent text-primary border-primary/30">
                      {nextWorkout.duration_minutes} min
                    </Badge>
                  </div>
                  {isShowingSelectedWorkout && (
                    <p className="text-sm text-gray-light mt-1">
                      Scheduled for {formatRelativeDate(nextWorkout.date)}
                    </p>
                  )}
                </div>
                
                {workoutPreviewItems.length > 0 && (
                  <div className="space-y-3 mb-6">
                    <h4 className="font-medium text-gray-700 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2 text-primary" />
                      Exercises included:
                    </h4>
                    <div className="space-y-2">
                      {workoutPreviewItems.map((grouping) => {
                        if (!grouping.label || grouping.exercises.length <= 1) {
                          const exercise = grouping.exercises[0];
                          if (!exercise) return null;

                          return (
                            <div
                              key={grouping.id}
                              className="flex items-center justify-between gap-3 rounded-lg border border-primary/20 bg-dark-slate px-3 py-2"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium leading-snug truncate sm:whitespace-normal sm:break-words">
                                  {exercise.name}
                                </div>
                              </div>
                              <div className="shrink-0 text-xs text-gray-light">
                                {exercise.sets} {exercise.sets === 1 ? "set" : "sets"}
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div
                            key={grouping.id}
                            className="overflow-hidden rounded-lg border border-primary/20 bg-dark-slate"
                          >
                            <div className="flex items-center justify-between gap-3 border-b border-primary/20 bg-primary/10 px-3 py-2">
                              <span className="text-sm font-semibold text-primary">
                                {grouping.label}
                              </span>
                              <span className="text-xs text-gray-light">
                                {grouping.rounds} {grouping.rounds === 1 ? "round" : "rounds"}
                              </span>
                            </div>
                            <div className="space-y-2 p-3">
                              {grouping.exercises.map((exercise, index) => (
                                <div key={exercise.id} className="space-y-2">
                                  {index > 0 && (
                                    <div className="text-center text-xs font-medium uppercase tracking-wide text-primary/80">
                                      Then
                                    </div>
                                  )}
                                  <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/15 bg-black/10 px-3 py-2">
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium leading-snug truncate sm:whitespace-normal sm:break-words">
                                        {exercise.name}
                                      </div>
                                    </div>
                                    <div className="shrink-0 text-xs text-gray-light">
                                      {exercise.sets} {exercise.sets === 1 ? "set" : "sets"}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <Button 
                  className="w-full gradient-primary text-black hover:bg-primary/90"
                  size="lg"
                  onClick={() => navigate(`/workout-session?assigned=${nextWorkout.id}`)}
                >
                  Start Now <ChevronRight className="ml-1 h-5 w-5 text-black" />
                </Button>
              </CardContent>
            </Card>
          ) : loadingWorkouts || !listReadyForSelection ? (
            <Card className={cn("mb-6 border-primary/20 bg-dark-card shadow-card overflow-hidden")}>
              <CardContent className="p-4">
                <div className="text-center py-8 text-gray-light">
                  Loading your next workout...
                </div>
              </CardContent>
            </Card>
          ) : assignedMissing ? (
            <Card className={cn("mb-6 border-primary/20 bg-dark-card shadow-card overflow-hidden")}>
              <CardContent className="p-4 space-y-4">
                <div className="text-center py-4">
                  <AlertTriangle className="mx-auto h-8 w-8 text-primary mb-3" />
                  <p className="font-medium text-white mb-1">Workout not found</p>
                  <p className="text-sm text-gray-light">
                    We couldn't load that workout. It may have been removed.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/start-workout")}
                >
                  View next workout
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className={cn("mb-6 border-primary/20 bg-dark-card shadow-card overflow-hidden")}>
              <CardContent className="p-4">
                <div className="text-center py-8">
                  <h3 className="font-bold text-lg mb-2">No Upcoming Workouts</h3>
                  <p className="text-gray-light text-sm mb-4">Check back later for your next workout assignment.</p>
                  <Link to="/client-plan">
                    <Button variant="outline" className="border-primary/30">
                      View Schedule
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Alternative options */}
          <div className="space-y-3">
            <h3 className="font-medium">Other Options</h3>
            
            <Button 
              variant="outline"
              className="w-full justify-between border-primary/30 text-left p-4 h-auto"
              onClick={startAiWorkoutGenerator}
            >
              <div className="flex items-center">
                <div className="bg-primary/20 p-2 rounded-full mr-3">
                  <MessageSquareText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <span className="font-medium block">Ask AI Trainer</span>
                  <span className="text-sm text-gray-light">Generate a custom workout</span>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </Button>
            
            <Link to="/client-plan">
              <Button 
                variant="outline"
                className="w-full justify-between border-primary/30 text-left p-4 h-auto"
              >
                <div className="flex items-center">
                  <div className="bg-primary/20 p-2 rounded-full mr-3">
                    <RotateCcw className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <span className="font-medium block">Change Workout</span>
                    <span className="text-sm text-gray-light">Pick a different scheduled workout</span>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartWorkout;
